import Entity from './entity';
import Position from './position';
import SpritePoly from './spritePoly';
import SpriteSolidMask from './spriteSolidMask';
import BrainSimpleText from './brainSimpleText';
import BrainSimpleButton from './brainSimpleButton';

export default class EntityFactory {

	constructor(args) {
		this.system = args.system;
		this.textGen = args.textGen || null;
		this.defaultColor = args.defaultColor || this.system.palette.green;
	}

	addToScene(entity) {
		this.getScene().entities.push(entity);
	}

	getScene() {
		return this.system.sceneManager.currentScene;
	}

	parseArgs(args) {
		if(typeof args.x == 'string') args.x = this.parseCoordinateExpression(args.x, this.system.screen.width);
		if(typeof args.y == 'string') args.y = this.parseCoordinateExpression(args.y, this.system.screen.height);
		return args;
	}

	/**
	 * Turns a String expression of a coordinate into a Number.
	 * Supports any number of relative percentages and absolute numbers.
	 * All operands are added together (use negative numbers for subtraction).
	 * Spaces required between all operands.
	 * Examples:
	 * '50%' -> halfway along the axis
	 * '50% 10' -> halfway along the axis plus 10 pixels
	 * '50% -10' -> halfway along the axis minus 10 pixels
	 * '-100% 80' -> one full axis length below zero plus 80 pixels 
	 * @param {String} expression 
	 * @param {Number} axis total length of the axis we're positioning within (eg. screen height)
	 * @returns {Number}
	 */
	parseCoordinateExpression(expression, axis) {
		let operands = expression.split(' ');
		let output = 0;
		for(let o of operands) {
			if(o.indexOf('%') > -1) {
				// '-52.3%' = axis * -0.523
				output += axis * parseFloat(o) * 0.01;
			} else {
				// '300' = 300
				output += parseFloat(o);
			}
		}
		return output;
	}





	/**
	 * Create a text entity and add it to the current scene
	 * @param {Object} args
	 * @param {String} args.name optional (internal object name)
	 * @param {Number} args.x
	 * @param {Number} args.y
	 * @param {Number} args.scale
	 * @param {Number} args.rotation
	 * @param {String} args.text use backslash-n for new lines
	 * @param {String} args.align left|right defaults to center
	 * @param {Number} args.color rgb hex color eg. 0x00ff00
	 * @returns {Entity}
	 */
	text(args) {
		args = this.parseArgs(args);
		let e = new Entity({
			name: args.name || 'text',
			scene: this.getScene(),
			sprite: new SpritePoly({
				layer: args.layer,
				position: new Position(args),
				shape: this.textGen.getTextPaths(args.text.toUpperCase()),
				align: args.align || false,
				color: args.color || this.defaultColor,
				charX: this.textGen.font.charX,
				charY: this.textGen.font.charY,
			}),
			brain: new BrainSimpleText(),
		});
		e.init();
		e.sprite.alpha = 0.5;
		this.addToScene(e);
		return e;
	}

	/**
	 * Create a non-physics polygon entity and add it to the current scene
	 * @param {Object} args
	 * @param {String} args.name optional (internal object name)
	 * @param {Number} args.x
	 * @param {Number} args.y
	 * @param {Number} args.scale
	 * @param {Number} args.rotation
	 * @param {Number} args.color rgb hex color eg. 0x00ff00
	 * @param {*} args.shape string or array of strings describing continuous lines of space-separated x y coords, eg. ['0 0 50 0 50 250 0 250','0 0 10 0 10 10 0 10']
	 * @param {Boolean} args.closeShapes draw line from end path vertex to start, of each shape?
	 * @param {Boolean} args.interactive respond to mouse/touch events?
	 * @param {*} args.brain optional
	 * @returns {Entity}
	 */
	poly(args) {
		args = this.parseArgs(args);
		let e = new Entity({
			name: args.name || 'poly',
			scene: this.getScene(),
			sprite: new SpritePoly({
				layer: args.layer,
				position: new Position(args),
				closeShapes: args.closeShapes || false,
				shape: args.shape || '50 -10 100 76.3 0 76.3 50 -10',
				color: args.color,
				interactive: args.interactive
			}),
			brain: args.brain || false,
		});
		e.init();
		this.addToScene(e);
		return e;
	}

	/**
	 * Create a simple button entity and add it to the current scene
	 * @param {Object} args
	 * @param {String} args.name optional (internal object name)
	 * @param {Function} args.onClick function to be called when the button is clicked
	 * @param {String} args.text button label
	 * @param {String} args.align left|right defaults to center
	 * @param {Number} args.x
	 * @param {Number} args.y
	 * @param {Number} args.scale
	 * @param {Number} args.color rgb hex color eg. 0x00ff00
	 * @returns {Entity}
	 */
	button(args) {
		args = this.parseArgs(args);
		let e = new Entity({
			name: args.name || 'button',
			scene: this.getScene(),
			sprite: new SpritePoly({
				layer: args.layer,
				position: new Position(args),
				shape: this.textGen.getTextPaths(args.text.toUpperCase()),
				border: 9,
				color: args.color || this.defaultColor,
				interactive: true,
				charX: this.textGen.font.charX,
				charY: this.textGen.font.charY,
				align: args.align || false,
			}),
			brain: new BrainSimpleButton({
				onClick: args.onClick, // pass an onclick function to a simple button brain
				hoverColor: args.hoverColor
			})
		});
		e.init();
		e.sprite.alpha = 0.5;
		this.addToScene(e);
		return e;
	}

	mask(args) {
		args = this.parseArgs(args);
		let e = new Entity({
			name: args.name || 'mask',
			scene: this.getScene(),
			sprite: new SpriteSolidMask({
				layer: args.layer,
				position: new Position(args),
				shape: args.shape,
				color: args.color || this.system.palette.bg,
			})
		});
		e.init();
		this.addToScene(e);
		return e;
	}

}